if (!customElements.get('ct-product-variant-cards')) {
  customElements.define(
    'ct-product-variant-cards',
    class CtProductVariantCards extends HTMLElement {
      constructor() {
        super();

        this.handleClick = this.handleClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange
          );
        }

        this.syncSelectedStateFromJson();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.variantChangeUnsubscriber?.();
      }

      get buttons() {
        return Array.from(this.querySelectorAll('.ct-product-variant-cards__card'));
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get nativeVariantSelects() {
        return this.querySelector('variant-selects');
      }

      get selectedVariantScript() {
        return this.nativeVariantSelects?.querySelector('[data-selected-variant]');
      }

      handleClick(event) {
        const button = event.target.closest('.ct-product-variant-cards__card');

        if (!button || !this.contains(button)) return;

        this.selectVariant(button);
      }

      handleVariantChange({ data }) {
        if (!this.productInfo || !data) return;

        if (data.sectionId && data.sectionId !== this.productInfo.sectionId) return;
        if (!data.variant || !data.variant.id) return;

        this.updateSelectedState(String(data.variant.id));
        this.updateProductFormVariantInputs(String(data.variant.id));
      }

      syncSelectedStateFromJson() {
        const selectedVariantScript = this.selectedVariantScript;

        if (!selectedVariantScript) return;

        try {
          const variant = JSON.parse(selectedVariantScript.textContent);

          if (variant?.id) {
            this.updateSelectedState(String(variant.id));
            this.updateProductFormVariantInputs(String(variant.id));
          }
        } catch (error) {
          console.error(error);
        }
      }

      selectVariant(button) {
        const variantId = button.dataset.variantId;

        if (!variantId) return;

        this.updateSelectedState(String(variantId));
        this.updateProductFormVariantInputs(String(variantId));

        const selectedOptions = [
          button.dataset.option1,
          button.dataset.option2,
          button.dataset.option3,
        ].filter((value) => typeof value !== 'undefined' && value !== '');

        this.dispatchEvent(
          new CustomEvent('ct:variant-selected', {
            bubbles: true,
            detail: {
              variantId: String(variantId),
              options: selectedOptions,
            },
          })
        );

        const nativeVariantSelects = this.nativeVariantSelects;

        if (!nativeVariantSelects) return;

        const selects = Array.from(nativeVariantSelects.querySelectorAll('select'));

        if (!selects.length) return;

        const targetValues = [
          button.dataset.option1,
          button.dataset.option2,
          button.dataset.option3,
        ];

        let changedSelect = null;

        selects.forEach((select, index) => {
          const targetValue = targetValues[index];

          if (typeof targetValue === 'undefined') return;

          const matchingOption = Array.from(select.options).find((option) => {
            return option.value === targetValue;
          });

          if (!matchingOption) return;

          Array.from(select.options).forEach((option) => {
            option.removeAttribute('selected');
          });

          matchingOption.selected = true;
          matchingOption.setAttribute('selected', 'selected');
          select.value = matchingOption.value;

          changedSelect = select;
        });

        if (!changedSelect) return;

        changedSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateSelectedState(selectedVariantId) {
        this.buttons.forEach((button) => {
          const isSelected = button.dataset.variantId === String(selectedVariantId);

          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      updateProductFormVariantInputs(variantId) {
        const productInfo = this.productInfo;

        if (!productInfo || !variantId) return;

        const variantInputs = productInfo.querySelectorAll('input[name="id"]');

        variantInputs.forEach((input) => {
          input.value = String(variantId);
          input.disabled = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
  );
}

(() => {
  const weightBadgeSelector = '[data-ct-variant-weight]';
  let variantChangeBound = false;

  const formatNumber = (value, maximumFractionDigits = 3) => {
    return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
      maximumFractionDigits,
    }).format(Number(value));
  };

  const weightFromText = (value) => {
    const match = String(value || '').match(/(\d+(?:[.,]\d+)?)\s*(kg|g)\b/i);
    if (!match) return '';

    return `${match[1]}${match[2].toLowerCase()}`;
  };

  const formatGrams = (grams) => {
    const numericGrams = Number(grams);
    if (!Number.isFinite(numericGrams) || numericGrams <= 0) return '';

    if (numericGrams >= 1000) {
      return `${formatNumber(numericGrams / 1000)}kg`;
    }

    return `${formatNumber(numericGrams, 0)}g`;
  };

  const formatWeightLabel = (variant) => {
    if (!variant) return '';

    const titleWeight =
      weightFromText(variant.title) ||
      weightFromText(variant.public_title) ||
      weightFromText(Array.isArray(variant.options) ? variant.options.join(' / ') : '');

    if (titleWeight) return titleWeight;

    if (Number(variant.grams) > 0) {
      return formatGrams(variant.grams);
    }

    const numericWeight = Number(variant.weight);
    const unit = String(variant.weight_unit || '').toLowerCase();

    if (Number.isFinite(numericWeight) && numericWeight > 0 && unit) {
      if (unit === 'g') return `${formatNumber(numericWeight, 0)}g`;
      if (unit === 'kg') return `${formatNumber(numericWeight)}kg`;
      return `${formatNumber(numericWeight)}${unit}`;
    }

    if (Number.isFinite(numericWeight) && numericWeight > 0) {
      return formatGrams(numericWeight);
    }

    return '';
  };

  const ensureWeightBadges = (productInfo) => {
    if (!productInfo) return [];

    const badges = [];
    const desktopTitle = productInfo.querySelector('.product__info-container > .product__title');
    const mobileHeading = productInfo.querySelector('.ct-product-mobile-heading');

    if (desktopTitle) {
      let badge = desktopTitle.querySelector(weightBadgeSelector);

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'ct-product-variant-weight';
        badge.dataset.ctVariantWeight = '';
        badge.hidden = true;
        desktopTitle.appendChild(badge);
      }

      badges.push(badge);
    }

    if (mobileHeading) {
      let row = mobileHeading.querySelector('.ct-product-title-row');
      let heading = row?.querySelector('h1') || Array.from(mobileHeading.children).find((element) => element.tagName === 'H1');

      if (!row && heading) {
        row = document.createElement('div');
        row.className = 'ct-product-title-row';
        heading.parentNode.insertBefore(row, heading);
        row.appendChild(heading);
      }

      if (row) {
        let badge = row.querySelector(weightBadgeSelector);

        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'ct-product-variant-weight';
          badge.dataset.ctVariantWeight = '';
          badge.hidden = true;
          row.appendChild(badge);
        }

        badges.push(badge);
      }
    }

    return badges;
  };

  const updateWeightBadges = (productInfo, variant) => {
    const label = formatWeightLabel(variant);
    const badges = ensureWeightBadges(productInfo);

    badges.forEach((badge) => {
      badge.textContent = label;
      badge.hidden = !label;
    });
  };

  const getInitialVariantFromJson = (productInfo) => {
    const selectedVariantScript = productInfo?.querySelector('variant-selects [data-selected-variant]');
    if (!selectedVariantScript?.textContent) return null;

    try {
      return JSON.parse(selectedVariantScript.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const fetchInitialVariant = async (productInfo) => {
    const productUrl = productInfo?.dataset.url;
    if (!productUrl) return null;

    try {
      const response = await fetch(`${productUrl}.js`, { credentials: 'same-origin' });
      if (!response.ok) return null;

      const product = await response.json();
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const selectedVariantId = String(productInfo.querySelector('input[name="id"]')?.value || '');

      return variants.find((variant) => String(variant.id) === selectedVariantId) || variants[0] || null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const initializeProductWeight = async (productInfo) => {
    if (!productInfo || productInfo.dataset.ctVariantWeightInitialized === 'true') return;

    productInfo.dataset.ctVariantWeightInitialized = 'true';
    ensureWeightBadges(productInfo);

    const variantFromJson = getInitialVariantFromJson(productInfo);
    const initialVariant = variantFromJson || await fetchInitialVariant(productInfo);

    if (initialVariant) {
      updateWeightBadges(productInfo, initialVariant);
    }
  };

  const initializeAllProductWeights = () => {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      initializeProductWeight(productInfo);
    });
  };

  const bindVariantChange = () => {
    if (variantChangeBound || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    variantChangeBound = true;

    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.variant) return;

      const productInfo = Array.from(document.querySelectorAll('product-info')).find((node) => {
        const sectionId = node.dataset.originalSection || node.dataset.section;
        return !data.sectionId || sectionId === data.sectionId;
      });

      if (productInfo) {
        updateWeightBadges(productInfo, data.variant);
      }
    });
  };

  document.addEventListener('ct:variant-selected', (event) => {
    const productInfo = event.target?.closest?.('product-info');
    if (!productInfo) return;

    const options = Array.isArray(event.detail?.options) ? event.detail.options : [];

    updateWeightBadges(productInfo, {
      id: event.detail?.variantId,
      title: options.join(' / '),
      options,
    });
  });

  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target?.closest?.('product-info') || event.target;
    initializeProductWeight(productInfo);
  });

  bindVariantChange();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindVariantChange();
      initializeAllProductWeights();
    }, { once: true });
  } else {
    bindVariantChange();
    initializeAllProductWeights();
  }

  document.addEventListener('shopify:section:load', () => {
    bindVariantChange();
    initializeAllProductWeights();
  });
})();


(() => {
  if (window.__ctAtmVariantBadgeInitialized) return;
  window.__ctAtmVariantBadgeInitialized = true;

  const rowSelector = '.ct-product-trust-row[data-ct-atm-variant-ids]';

  const updateAtmBadge = (variantId) => {
    if (!variantId) return;

    document.querySelectorAll(rowSelector).forEach((row) => {
      const enabledVariantIds = String(row.dataset.ctAtmVariantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      const isEnabled = enabledVariantIds.includes(String(variantId));
      row.classList.toggle('ct-product-trust-row--with-atm', isEnabled);

      const atm = row.querySelector('.ct-product-atm');
      if (!atm) return;

      atm.hidden = !isEnabled;
      if (!isEnabled) atm.removeAttribute('open');
    });
  };

  const syncInitialAtmBadge = () => {
    const variantInput = document.querySelector('product-info input[name="id"]');
    if (variantInput?.value) updateAtmBadge(variantInput.value);
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (data?.variant?.id) updateAtmBadge(data.variant.id);
    });
  }

  document.addEventListener('ct:variant-selected', (event) => {
    if (event.detail?.variantId) updateAtmBadge(event.detail.variantId);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncInitialAtmBadge, { once: true });
  } else {
    syncInitialAtmBadge();
  }

  document.addEventListener('shopify:section:load', syncInitialAtmBadge);
})();
